import { Flex, Button } from "@tremor/react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import * as userAPI from "../../services/user";
import { useEffect, useState } from "react";
import ThemedCodeEditor from "../../components/ThemedCodeEditor";
import rehypePrism from "@mapbox/rehype-prism";
const fieldStyle =
  "appearance-none border rounded w-full py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-tight bg-gray-50 dark:bg-gray-800";

const exampleCode = `
#define CAYENNE_PRINT Serial
// Zafron MQTT Host
#define CAYENNE_DOMAIN "mqtt.zafron.dev"

#include <CayenneMQTTESP8266.h>

// Zafron MQTT credentials info.
char username[] = "VAR_USERNAME";
char password[] = "VAR_PASSWORD";
char clientID[] = "VAR_CLIENTID";

void setup() {
  Serial.begin(9600);
  Cayenne.begin(username, password, clientID, ssid, wifiPassword);
}
`;

export default function Credentials({ clientId, showExampleCode }) {
  const [showTooltip, setShowTooltip] = useState("");
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });

  useEffect(() => {
    const getCredentials = async () => {
      try {
        const data = await userAPI.getUserInfo();
        if (!data.mqttCredentials) throw new Error("No MQTT credentials found");

        setCredentials(data.mqttCredentials);
      } catch (err) {
        console.error("Error fetching credentials:", err);
      }
    };
    getCredentials();
  }, []);

  const getExampleCode = () => {
    // replace the placeholders with the actual credentials
    exampleCode.replace("VAR_USERNAME", credentials.username);
    exampleCode.replace("VAR_PASSWORD", credentials.password);
    exampleCode.replace("VAR_CLIENTID", clientId);
    return exampleCode;
  };

  const copyToClipboard = (field, value) => {
    navigator.clipboard.writeText(value);
    setShowTooltip(field);
    setTimeout(() => setShowTooltip(""), 2000);
    // You can add some visual feedback here if you like
  };

  return (
    <div>
      <Flex alignItems="start" className="flex-col lg:flex-row gap-4 lg:gap-5">
        <div className={`credentials ${showExampleCode ? "w-full lg:w-1/2" : ""}`}>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-bold mb-1">
                Host
              </label>
              <Flex className="relative">
                <pre className={`${fieldStyle} rounded-r-none pr-10`}>mqtt.zafron.dev</pre>
                <button
                  onClick={() => copyToClipboard("host", "mqtt.zafron.dev")}
                  className="absolute right-0 inset-y-0 flex items-center px-2 sm:px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <ClipboardDocumentIcon className="h-4 sm:h-5 w-4 sm:w-5" />

                  {showTooltip === "host" && (
                    <div className="absolute bottom-full mb-2 right-0 bg-black text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                      Copied!
                    </div>
                  )}
                </button>
              </Flex>
            </div>

            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-bold mb-1">
                Port
              </label>

              <Flex className="relative">
                <pre className={`${fieldStyle} rounded-r-none pr-10`}>1883</pre>
                <button
                  onClick={() => copyToClipboard("port", "1883")}
                  className="absolute right-0 inset-y-0 flex items-center px-2 sm:px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <ClipboardDocumentIcon className="h-4 sm:h-5 w-4 sm:w-5" />

                  {showTooltip === "port" && (
                    <div className="absolute bottom-full mb-2 right-0 bg-black text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                      Copied!
                    </div>
                  )}
                </button>
              </Flex>
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-bold mb-1">
                Username
              </label>
              <Flex className="relative">
                <pre className={`${fieldStyle} rounded-r-none pr-10`}>{credentials.username}</pre>
                <button
                  onClick={() =>
                    copyToClipboard("username", credentials.username)
                  }
                  className="absolute right-0 inset-y-0 flex items-center px-2 sm:px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <ClipboardDocumentIcon className="h-4 sm:h-5 w-4 sm:w-5" />

                  {showTooltip === "username" && (
                    <div className="absolute bottom-full mb-2 right-0 bg-black text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                      Copied!
                    </div>
                  )}
                </button>
              </Flex>
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-bold mb-1">
                Password
              </label>
              <Flex className="relative">
                <pre className={`${fieldStyle} rounded-r-none pr-10`}>{credentials.password}</pre>
                <button
                  onClick={() =>
                    copyToClipboard("password", credentials.password)
                  }
                  className="absolute right-0 inset-y-0 flex items-center px-2 sm:px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <ClipboardDocumentIcon className="h-4 sm:h-5 w-4 sm:w-5" />

                  {showTooltip === "password" && (
                    <div className="absolute bottom-full mb-2 right-0 bg-black text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                      Copied!
                    </div>
                  )}
                </button>
              </Flex>
            </div>
            <div className="mb-3 sm:mb-4">
              <label className="block text-gray-700 dark:text-gray-300 text-xs sm:text-sm font-bold mb-1">
                Client Id
              </label>
              <Flex className="relative">
                <pre className={`${fieldStyle} rounded-r-none pr-10`}>{clientId}</pre>
                <button
                  onClick={() => copyToClipboard("clientid", clientId)}
                  className="absolute right-0 inset-y-0 flex items-center px-2 sm:px-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <ClipboardDocumentIcon className="h-4 sm:h-5 w-4 sm:w-5" />

                  {showTooltip === "clientid" && (
                    <div className="absolute bottom-full mb-2 right-0 bg-black text-white text-xs rounded py-1 px-2 z-10 whitespace-nowrap">
                      Copied!
                    </div>
                  )}
                </button>
              </Flex>
            </div>
          </form>
        </div>
        {showExampleCode && (
          <div className="w-full lg:w-1/2">
            <p className="tremor-default-content text-gray-500 dark:text-gray-400 text-xs sm:text-sm mb-1">
              Example Arduino Code
            </p>
            <ThemedCodeEditor
              value={getExampleCode()}
              language="arduino"
              readOnly={true}
              padding={15}
              rehypePlugins={[[rehypePrism, { ignoreMissing: true }]]}
              style={{
                overflow: "auto",
                borderRadius: 5,
                border: "1px solid #e1e1e1",
                backgroundColor: "#333",
                fontFamily:
                  "ui-monospace,SFMono-Regular,SF Mono,Consolas,Liberation Mono,Menlo,monospace",
              }}
            />

            <Flex justifyContent="end">
              <Button
                className="mt-2"
                variant="secondary"
                onClick={() => copyToClipboard("", getExampleCode())}
              >
                Copy to Clipboard
              </Button>
            </Flex>
          </div>
        )}
      </Flex>
    </div>
  );
}